/*
 * TOMYRIS CUSTOM: поле «Промокод или подарочная карта» в выезжающей корзине.
 * Разметка: snippets/tomyris-cart-discount.liquid.
 *
 * Одно поле, как в checkout. Код пробуется по очереди:
 * 1. Промокод — Cart API: POST /cart/update.js { discount }. Параметр ЗАМЕНЯЕТ весь список
 *    кодов, поэтому передаём уже применённые вместе с новым. Что с чем складывается, решает
 *    Shopify по настройкам скидок. Так же сделано в Horizon (assets/cart-discount.js).
 * 2. Подарочная карта — Storefront API (без токена, та же корзина): cartGiftCardCodesAdd.
 *    Cart API карты не принимает — для Shopify это способ оплаты, а не скидка.
 *    Снимаем только cartGiftCardCodesRemove: на форуме Shopify dev (авг 2025) описано,
 *    что cartGiftCardCodesUpdate с пустым списком оставляет карту в checkout.
 * Проверено 22 сен 2026 с настоящей картой: доезжает в checkout, переживает смену количества
 * и промокод, снимается, подхватывается уже открытым checkout.
 *
 * Карты Liquid не видит — их плашки (в общий ряд с промокодами) и «Сумма» за вычетом
 * карт дорисовываются после каждого пересчёта корзины (on:cart:after-merge).
 */
(() => {
  if (window.customElements.get('tomyris-cart-discount')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const updateUrl = (window.theme && theme.routes && theme.routes.cartUpdate) || `${root}cart/update.js`;
  const cartUrl = `${root}cart.js`;
  // Storefront API живёт на корне домена, без префикса языка (/kk/)
  const storefrontUrl = '/api/2025-10/graphql.json';

  const json = (r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  };
  const getCart = () => fetch(cartUrl, { headers: { Accept: 'application/json' } }).then(json);

  const getCodes = () => getCart()
    .then((cart) => (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code));

  const setCodes = (codes) => fetch(updateUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') })
  }).then(json);

  const storefront = async (query, variables) => {
    const cart = await getCart();
    const data = await fetch(storefrontUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables: { id: `gid://shopify/Cart/${cart.token}`, ...variables } })
    }).then(json);
    if (data.errors) throw new Error(data.errors.map((e) => e.message).join('; '));
    return { cart, data: data.data };
  };

  const GIFTS = 'appliedGiftCards { id lastCharacters amountUsed { amount } }';
  const getGifts = () => storefront(`query($id: ID!) { cart(id: $id) { ${GIFTS} } }`)
    .then(({ cart, data }) => ({ cart, gifts: data.cart ? data.cart.appliedGiftCards : [] }));
  const addGift = (code) => storefront(
    `mutation($id: ID!, $codes: [String!]!) { cartGiftCardCodesAdd(cartId: $id, giftCardCodes: $codes) { cart { ${GIFTS} } } }`,
    { codes: [code] }
  ).then(({ data }) => data.cartGiftCardCodesAdd.cart ? data.cartGiftCardCodesAdd.cart.appliedGiftCards : []);
  const removeGift = (giftId) => storefront(
    `mutation($id: ID!, $ids: [ID!]!) { cartGiftCardCodesRemove(cartId: $id, appliedGiftCardIds: $ids) { userErrors { message } } }`,
    { ids: [giftId] }
  );

  const notifyCartChanged = () => {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  };

  // shop.money_format, напр. «{{amount_no_decimals_with_space_separator}} ₸»
  const formatMoney = (cents, format) => format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
    const noDecimals = key.includes('no_decimals');
    const [int, dec] = (cents / 100).toFixed(noDecimals ? 0 : 2).split('.');
    let thousands = ',';
    let decimal = '.';
    if (key.includes('space_separator')) { thousands = ' '; decimal = ','; }
    else if (key.includes('comma_separator')) { thousands = '.'; decimal = ','; }
    else if (key.includes('apostrophe_separator')) { thousands = "'"; }
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dec ? `${grouped}${decimal}${dec}` : grouped;
  });

  class TomyrisCartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.input = this.querySelector('.tomyris-cart-discount__input');
      this.applyBtn = this.querySelector('.tomyris-cart-discount__apply');
      this.msg = this.querySelector('.tomyris-cart-discount__msg');
      this.renderSeq = 0;

      this.applyBtn.addEventListener('click', () => this.apply());
      this.input.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') {
          evt.preventDefault();
          this.apply();
        }
      });
      this.input.addEventListener('input', () => {
        if (this.msg.dataset.state === 'error') this.showMessage('');
        this.syncApply();
      });
      // плашки живут в merge-зоне и перерисовываются — поэтому делегирование
      document.addEventListener('click', (evt) => {
        const btn = evt.target.closest('[data-tomyris-gift-remove]');
        if (btn) this.removeGiftCard(btn);
      });
      // стоковый cart-form перерисовал сумму — снова вычитаем карты
      document.addEventListener('on:cart:after-merge', () => this.renderGifts());
      this.syncApply();
      this.renderGifts();
    }

    // Как в checkout: «Применить» активна, только когда код введён
    syncApply() {
      this.applyBtn.disabled = this.busy || !this.input.value.trim();
    }

    showMessage(text, state) {
      this.msg.textContent = text;
      this.msg.hidden = !text;
      this.msg.dataset.state = state || '';
    }

    setBusy(busy) {
      this.busy = busy;
      this.input.disabled = busy;
      this.syncApply();
      this.classList.toggle('is-busy', busy);
    }

    async applyDiscount(code) {
      const same = (c) => c.toLowerCase() === code.toLowerCase();
      const previous = await getCodes();
      if (previous.some(same)) return true;
      const cart = await setCodes([...previous, code]);
      const applied = (cart.discount_codes || []).some((d) => d.applicable && same(d.code));
      // не сработавший код не оставляем: в checkout он висел бы ошибкой
      if (!applied) await setCodes(previous);
      return applied;
    }

    async applyGiftCard(code) {
      const tail = code.replace(/[\s-]/g, '').slice(-4).toLowerCase();
      const gifts = await addGift(code.replace(/[\s-]/g, ''));
      return gifts.some((g) => g.lastCharacters.toLowerCase() === tail);
    }

    async apply() {
      const code = this.input.value.trim();
      if (!code) return;

      this.setBusy(true);
      this.showMessage('');
      try {
        let applied = await this.applyDiscount(code);
        if (!applied) {
          try {
            applied = await this.applyGiftCard(code);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('tomyris-cart-discount: gift card', error);
          }
        }

        if (applied) {
          this.input.value = '';
        } else {
          this.showMessage(this.dataset.msgError, 'error');
        }
        notifyCartChanged();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('tomyris-cart-discount', error);
        this.showMessage(this.dataset.msgError, 'error');
      } finally {
        this.setBusy(false);
      }
    }

    async removeGiftCard(btn) {
      btn.disabled = true;
      try {
        // id применённой карты меняется при каждом пересчёте корзины — берём свежий по последним символам
        const { gifts } = await getGifts();
        const gift = gifts.find((g) => g.lastCharacters === btn.dataset.tomyrisGiftRemove);
        if (gift) await removeGift(gift.id);
        this.showMessage('');
        notifyCartChanged();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('tomyris-cart-discount: gift card remove', error);
        btn.disabled = false;
      }
    }

    async renderGifts() {
      const seq = ++this.renderSeq;
      let result;
      try {
        result = await getGifts();
      } catch (error) {
        return; // Storefront API недоступен — промокоды работают и без этого
      }
      if (seq !== this.renderSeq) return;

      const { cart, gifts } = result;
      const drawer = this.closest('cart-drawer');
      const chips = drawer && drawer.querySelector('[data-tomyris-chips]');
      if (!chips) return;
      chips.querySelectorAll('[data-tomyris-gift]').forEach((li) => li.remove());
      let usedTotal = 0;
      gifts.forEach((g) => {
        usedTotal += Math.min(Math.round(parseFloat(g.amountUsed.amount) * 100), cart.total_price);
        const last = g.lastCharacters.toUpperCase();
        const li = document.createElement('li');
        li.className = 'tomyris-cart-discount__chip';
        li.dataset.tomyrisGift = '';
        li.innerHTML = `<span class="tomyris-cart-discount__chip-icon">${this.querySelector('[data-tomyris-gift-icon]').innerHTML}</span>
          <span class="tomyris-cart-discount__chip-text"></span>
          <button type="button" class="tomyris-cart-discount__remove">${this.querySelector('[data-tomyris-close-icon]').innerHTML}</button>`;
        const text = li.querySelector('.tomyris-cart-discount__chip-text');
        text.textContent = `•••• ${last}`;
        text.setAttribute('aria-label', `${this.dataset.giftLabel} •••• ${last}`);
        const btn = li.querySelector('button');
        btn.dataset.tomyrisGiftRemove = g.lastCharacters;
        btn.setAttribute('aria-label', `${this.dataset.removeLabel}: •••• ${last}`);
        chips.appendChild(li);
      });

      // «Сумма» из Liquid про карты не знает — показываем к оплате, как «Итого к оплате» в checkout
      const subtotal = drawer.querySelector('.subtotal .theme-money');
      if (subtotal && gifts.length) subtotal.textContent = formatMoney(Math.max(cart.total_price - usedTotal, 0), this.dataset.moneyFormat);
    }
  }

  window.customElements.define('tomyris-cart-discount', TomyrisCartDiscount);

  // Крестик у применённого промокода живёт в merge-зоне и перерисовывается — поэтому делегирование
  document.addEventListener('click', async (evt) => {
    const btn = evt.target.closest('[data-tomyris-discount-remove]');
    if (!btn) return;
    evt.preventDefault();
    btn.disabled = true;
    const target = btn.dataset.tomyrisDiscountRemove.toLowerCase();
    try {
      const codes = await getCodes();
      await setCodes(codes.filter((c) => c.toLowerCase() !== target));
      document.querySelectorAll('tomyris-cart-discount').forEach((el) => el.showMessage(''));
      notifyCartChanged();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('tomyris-cart-discount', error);
      btn.disabled = false;
    }
  });
})();
